// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Search Overlay (⌘K)
// Searches articles, tasks, customers, glossary with category filtering
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useMemo } from 'react';
import { useApp, CUSTOMERS, GLOSSARY } from '../../hooks/useAppState';
import { theme, fonts } from '../../lib/theme';
import { Badge } from '../shared';

export function SearchOverlay() {
  const { state, dispatch, navigate, getAllTasks } = useApp();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const q = query.toLowerCase();

  const results = useMemo(() => {
    if (!q) return [];
    const items: Array<{ type: string; icon: string; title: string; sub: string; action: () => void }> = [];

    // Articles
    state.articles.filter(a => a.status === 'published').forEach(a => {
      if (a.title.toLowerCase().includes(q) || a.tags.some(t => t.toLowerCase().includes(q)) ||
          a.mitreTechniques.some(t => t.toLowerCase().includes(q))) {
        const cat = state.categories.find(c => c.id === a.categoryId);
        items.push({
          type: 'Article', icon: cat?.icon || '📄', title: a.title,
          sub: `${cat?.name || ''} · ${a.tags.slice(0, 3).join(', ')}`,
          action: () => { dispatch({ type: 'SELECT_ARTICLE', id: a.id }); navigate('article', a.id); dispatch({ type: 'SET_SEARCH_OPEN', open: false }); },
        });
      }
    });

    // Tasks
    getAllTasks().forEach(t => {
      if (t.title.toLowerCase().includes(q)) {
        const proj = state.projects.find(p => p.id === t.projectId);
        items.push({
          type: 'Task', icon: '📋', title: t.title,
          sub: `${proj?.name || ''} · ${t.status}`,
          action: () => { navigate('tasks', t.id); dispatch({ type: 'SET_SEARCH_OPEN', open: false }); },
        });
      }
    });

    // Customers
    CUSTOMERS.forEach(cu => {
      if (cu.name.toLowerCase().includes(q)) {
        items.push({
          type: 'Customer', icon: '🏢', title: cu.name,
          sub: `${cu.m365Tier} · MDE ${cu.mdePlan} · MDO ${cu.mdoPlan}`,
          action: () => { navigate('customers'); dispatch({ type: 'SET_SEARCH_OPEN', open: false }); },
        });
      }
    });

    // Glossary
    GLOSSARY.forEach(g => {
      if (g.acronym.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q)) {
        items.push({
          type: 'Glossary', icon: '📖', title: g.acronym,
          sub: g.definition.slice(0, 80) + '…',
          action: () => { navigate('glossary'); dispatch({ type: 'SET_SEARCH_OPEN', open: false }); },
        });
      }
    });

    return items.slice(0, 12);
  }, [q, state.articles, state.projects]);

  return (
    <div
      onClick={() => dispatch({ type: 'SET_SEARCH_OPEN', open: false })}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)', zIndex: 9999,
        display: 'flex', justifyContent: 'center', paddingTop: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 560, maxHeight: 480, background: theme.card,
          border: `1px solid ${theme.border}`, borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${theme.border}` }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.textTertiary} strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search articles, tasks, customers, glossary..."
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: theme.text, fontSize: 15, fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 10, color: theme.textTertiary, background: theme.bg2, padding: '2px 8px', borderRadius: 4 }}>ESC</span>
        </div>

        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {!q && (
            <div style={{ padding: 30, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>
              Type to search across the entire platform
            </div>
          )}
          {q && results.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>
              No results for "{query}"
            </div>
          )}
          {results.map((r, i) => (
            <div
              key={i}
              onClick={r.action}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                cursor: 'pointer', borderBottom: `1px solid ${theme.border}`,
                transition: 'background .1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = theme.cardHover)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{r.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                <div style={{ fontSize: 10, color: theme.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</div>
              </div>
              <Badge sm color={theme.textTertiary}>{r.type}</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
